import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RadioGroup } from "@headlessui/react";

const Options = ({ questionIndex }) => (
  <div className="flex flex-row-reverse items-stretch justify-between gap-2">
    {["None", "Some", "Half", "Most", "All"].map((opt, idx) => (
      <RadioGroup.Option
        key={`q-${questionIndex}-${idx}`}
        value={idx}
        className={({ checked }) =>
          `flex-1 px-1.5 py-2 mb-[10px] mt-[5px] rounded-lg border border-secondary/20 ${
            checked ? "bg-[#53abff] text-white" : "bg-white/20 text-white"
          } text-center text-xxs font-medium leading-[14px]`
        }
      >
        {opt} of the time
      </RadioGroup.Option>
    ))}
  </div>
);

const Survey = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [sectionA, setSectionA] = useState([0, 0, 0, 0, 0]);
  const [sectionB, setSectionB] = useState([-1, -1, -1, -1]);
  const [sectionC, setSectionC] = useState([-1, -1, -1]);

  const calculateOSDI = () => {
    const filteredAns = [...sectionA, ...sectionB, ...sectionC].filter(
      (a) => a !== -1
    );
    const sum = filteredAns.reduce((s, v) => s + v, 0);
    const osdi = (sum * 25) / filteredAns.length;
    navigate(
      state?.selectedAssesments?.pulmo === true
        ? "/voice-audio-sample"
        : state?.selectedAssesments?.dgmer === true
        ? "/digimer-assessment"
        : "/all-combined-result",
      {
        state: {
          ...state,
          visionResult: { ...(state?.visionResult ?? {}), osdi },
        },
        replace: true,
      }
    );
  };

  return (
    <main className="px-6 pt-0 pb-20 space-y-8 -mt-6 tab-screen">
      <div className="circle-blur w-[80%] h-60 mx-auto absolute top-20 left-0 right-0 blur-[70px]"></div>
      <h1 className="text-white text-left text-xl font-semibold relative z-50">
        Ocular Surface Disease Index Questionnaire
      </h1>

      <section className="relative z-50">
        <h4 className="text-secondary font-medium mb-3">
          Have you experienced any of the following during the last week:
        </h4>
        {[
          "Eyes that are sensitive to light?",
          "Eyes that feel gritty?",
          "Painful or sore eyes?",
          "Blurred vision?",
          "Poor vision?",
        ].map((q, qIdx) => (
          <RadioGroup
            key={`a-${qIdx}`}
            className="mt-2 space-y-1.5"
            value={sectionA[qIdx]}
            onChange={(v) =>
              setSectionA((ans) => {
                ans[qIdx] = v;
                return [...ans];
              })
            }
          >
            <RadioGroup.Label className="text-white text-sm">
              {q}
            </RadioGroup.Label>
            <Options questionIndex={qIdx} />
          </RadioGroup>
        ))}
      </section>

      <section>
        <h4 className="text-secondary font-medium mb-3">
          Have problems with your eyes limited you in performing any of the
          following during the last week:
        </h4>
        {[
          "Reading?",
          "Driving at night?",
          "Working with a computer or bank machine (ATM)?",
          "Watching TV?",
        ].map((q, qIdx) => (
          <RadioGroup
            key={`b-${qIdx}`}
            className="mt-3 space-y-1.5"
            value={sectionB[qIdx]}
            onChange={(v) =>
              setSectionB((ans) => {
                ans[qIdx] = v;
                return [...ans];
              })
            }
          >
            <RadioGroup.Label className="text-white text-sm">
              {q}
            </RadioGroup.Label>
            <Options questionIndex={qIdx} />
          </RadioGroup>
        ))}
      </section>

      <section>
        <h4 className="text-secondary font-medium mb-3">
          Have your eyes felt uncomfortable in any of the following situations
          during the last week:
        </h4>
        {[
          "Windy conditions?",
          "Places or areas with low humidity (very dry)?",
          "Areas that are air conditioned?",
        ].map((q, qIdx) => (
          <RadioGroup
            key={`c-${qIdx}`}
            className="mt-3 space-y-1.5"
            value={sectionC[qIdx]}
            onChange={(v) =>
              setSectionC((ans) => {
                ans[qIdx] = v;
                return [...ans];
              })
            }
          >
            <RadioGroup.Label className="text-white text-sm">
              {q}
            </RadioGroup.Label>
            <Options questionIndex={qIdx} />
          </RadioGroup>
        ))}
      </section>

      <section className="grid grid-cols-2 gap-6">
        <button
          type="button"
          className="block w-full px-6 py-3 rounded-full outline-none border border-white/80 text-white text-sm font-medium"
          onClick={() => navigate(-3)}
        >
          Cancel
        </button>
        <button
          type="button"
          className="block w-full px-6 py-3 rounded-full outline-none bg-white disabled:bg-[#808080] text-primary text-sm font-medium"
          onClick={calculateOSDI}
        >
          Next
        </button>
      </section>
    </main>
  );
};

export default Survey;
